import {
    EmptyTopBar,
    Loading,
    SelectButtons,
    TwoColumnLayout,
} from '../../components';
import { useTranslation } from 'react-i18next';
import i18nNamespaces from '../../const/i18nNamespaces';
import { Box, Button, FormControlLabel, Radio, RadioGroup } from '@material-ui/core';
import useStyles from './NewPrescriptionPageStyles';
import NewPrescriptionForm from '../../components/prescription/NewPrescriptionForm/NewPrescriptionForm';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Prescription, IssuePrescriptionResponse } from '../../types/prescription';
import { v4 } from 'uuid';
import prescriptionsApi from '../../api/prescriptions/prescriptionsApi';
import AppointmentsProvider from '../../services/appointmentsProvider';
import { AppointmentDetailsForPrescriptionDto, BusinessRoleEnum } from '../../types/appointments';
import { PRESCRIPTION_ISSUED_PAGE } from '../../const/routes';
import { useHistory, useParams } from 'react-router';
import PrescriptionItem, {
    SelectedPrescription,
} from '../../components/prescription/PrescriptionItem/PrescriptionItem';
import {
    isPrescriptionFilled,
    mapAppointmentDetailsToPrescription,
    mapPrescriptionDraftToSelectedPrescription,
    mapPrescriptionToIssuePrescription,
} from '../../utils/prescription';
import { SelectButtonOption } from '../../components/common/SelectButtons/SelectButtons';
import { AssignmentOutlined, HistoryOutlined, LocalHospitalOutlined } from '@material-ui/icons';
import PrescriptionHistory from '../../components/prescription/PrescriptionHistory/PrescriptionHistory';
import { AlertContext } from '../../contexts/AlertContext';
import useQuery from '../../hooks/useQuery';
import EditPrescriptionForm from '../../components/prescription/EditPrescriptionForm/EditPrescriptionForm';
import { P1Response } from '../../types/p1Service';
import PrescriptionP1MessagePopup from '../../components/prescription/PrescriptionP1MessagePopup/PrescriptionP1MessagePopup';
import sentryService from '../../services/sentryService';
import EwusTab from '../../components/prescription/EwusTab/EwusTab';
import { useAppDispatch, useAppSelector } from '../../hooks/customReduxHooks';
import { fetchEwusInitialize, fetchPatientNFZRegionalBranchCode, selectEwusStatus } from '../../store/ewusSlice';
import PatientInsurancePill from '../../components/prescription/PatientInsurancePill/PatientInsurancePill';
import PrescriptionTabsContent from '../../components/prescription/PrescriptionTabsContent/PrescriptionTabsContent';

const PRESCRIPTION_TYPES = {
    ORDINATION: 0,
    CONTINUATION: 1,
} as const;

type PrescriptionType = typeof PRESCRIPTION_TYPES[keyof typeof PRESCRIPTION_TYPES];

const tabs: SelectButtonOption[] = [
    {
        value: 'DRUG_DATABASE',
        label: 'drugDatabase',
        icon: <LocalHospitalOutlined />,
    },
    {
        value: 'EWUS',
        label: 'ewus',
        icon: <AssignmentOutlined />,
    },
    {
        value: 'PRESCRIPTION_HISTORY',
        label: 'prescriptionHistory',
        icon: <HistoryOutlined />,
    }
];

const KEEP_MOUNTED_TAB_VALUES: SelectButtonOption['value'][] = ['DRUG_DATABASE', 'EWUS'];

const NewPrescriptionPage = () => {
    const { t } = useTranslation(i18nNamespaces.PRESCRIPTIONS);
    const { t: errorT } = useTranslation(i18nNamespaces.ERRORS);
    const dispatch = useAppDispatch();
    const classes = useStyles();
    const params = useParams<{ appointmentId: string }>();
    const query = useQuery();
    const appointmentId = parseInt(params?.appointmentId);
    const history = useHistory();
    const [prescriptions, setPrescriptions] = useState<SelectedPrescription[]>(
        [],
    );
    const [loading, setLoading] = useState<boolean>(false);
    const [appointmentDetails, setAppointmentDetails] =
        useState<AppointmentDetailsForPrescriptionDto | null>(null);
    const [selectedTab, setSelectedTab] = useState<SelectButtonOption>(tabs[0]);
    const patientOrChildDetails =
        appointmentDetails?.child || appointmentDetails?.patient;
    const topbarTitle = `${t('newPrescriptionFor')} ${patientOrChildDetails?.firstName
        } ${patientOrChildDetails?.surname}`;
    const tabsWithTranslations = tabs?.map(tab => ({
        ...tab,
        label: t(tab.label),
    }));
    const openAlert = useContext(AlertContext);
    const isFromDraft = query.get('fromDraft') === 'true';
    const [prescriptionDraftId, setPrescriptionDraftId] = useState<string | null>(null);
    const [prescriptionInEditId, setPrescriptionInEditId] = useState<string | null>(null);
    const { prescriptionInEdit, prescriptionInEditIndex } = useMemo(() => {
        const index = prescriptions.findIndex(p => p.id === prescriptionInEditId);
        return {
            prescriptionInEdit: index === -1 ? undefined : prescriptions[index],
            prescriptionInEditIndex: index === -1 ? prescriptions.length + 1 : index + 1,
        };
    }, [prescriptions, prescriptionInEditId]);
    const isMedicNurse = appointmentDetails?.medic.businessRole === BusinessRoleEnum.PIEL;
    const [prescriptionType, setPrescriptionType] = useState<PrescriptionType>(PRESCRIPTION_TYPES.CONTINUATION);
    const [issuePrescriptionResponse, setIssuePrescriptionResponse] = useState<P1Response<IssuePrescriptionResponse> | null>(null);
    const [showP1MessagePopup, setShowP1MessagePopup] = useState(false);
    const ewusLoadingStatus = useAppSelector(selectEwusStatus);
    const findTab = (tabValue: SelectButtonOption['value']) => tabs.find(tab => tab.value === tabValue) || tabs[0];

    /* NFZ Insurance Declaration and EWUS verification initialization */
    useEffect(() => {
        if(!patientOrChildDetails || !appointmentDetails?.id) return;
        dispatch(fetchEwusInitialize({
            appointmentId: appointmentDetails.id,
            patientPesel: patientOrChildDetails.pesel,
        }));
    }, [patientOrChildDetails, appointmentDetails?.id]);

    useEffect(() => {
        if(!appointmentDetails?.patient?.userId) return;
        dispatch(fetchPatientNFZRegionalBranchCode({ patientId: appointmentDetails?.patient?.userId }));
    }, [appointmentDetails?.patient?.userId]);


    useEffect(() => {
        (async () => {
            try {
                setLoading(true);
                const { data } =
                    await AppointmentsProvider.getAppointmentDetailsForPrescription(
                        appointmentId.toString(),
                    );
                setAppointmentDetails(data);
            } catch (error) {
                console.error(error);
            } finally {
                setLoading(false);
            }
        })();
    }, [appointmentId]);

    useEffect(() => {
        if (!isFromDraft) return;
        fetchPrescriptionDraft();
    }, []);

    const fetchPrescriptionDraft = async () => {
        try {
            setLoading(true);
            const { data } = await prescriptionsApi.getPrescriptionDraft(appointmentId);
            setPrescriptionDraftId(data.result?.id);
            setPrescriptions(data.result?.prescriptions.map(mapPrescriptionDraftToSelectedPrescription));
        } catch (error) {
            console.error(error);
            openAlert({
                severity: 'error',
                message: errorT('unexpectedErrorOccurred'),
            });
        } finally {
            setLoading(false);
        }
    };

    const handleSubmitPrescriptionForm = (prescription: Prescription) => {
        if(prescriptions.length >= 5){
            return openAlert({
                severity: 'error',
                message: t('maxPrescriptionsReached'),
            });
        }
        setPrescriptions(prev => [...prev, { ...prescription, id: v4() }]);
    };

    const cleanPrescriptionErrors = (prescriptionId: string) => {
        setIssuePrescriptionResponse(prev => {
            if (!prev || !prev.result || !Array.isArray(prev.result.results)) return prev;
            const idx = prescriptions.findIndex(p => p.id === prescriptionId);
            if (idx === -1) return prev;
            return {
                ...prev,
                result: {
                    ...prev.result,
                    results: prev.result.results.filter(r => (r.order - 1) !== idx)
                }
            };
        });
    }

    const handleSubmitEditPrescriptionForm = (prescription: SelectedPrescription) => {
        setPrescriptions(p => p.map(p => p.id === prescription.id ? prescription : p));
        setPrescriptionInEditId(null);
        cleanPrescriptionErrors(prescription.id);
    }

    const handleCancelEditPrescriptionForm = () => {
        setPrescriptionInEditId(null);
    }

    const handleDeletePrescription = (prescriptionId: string) => {
        setPrescriptions(prev =>
            prev.filter(prescription => prescription.id !== prescriptionId),
        );
    };

    const handleEditPrescriptionItem = (prescriptionId: string) => {
        setPrescriptionInEditId(prescriptionId);
        setSelectedTab(findTab('DRUG_DATABASE'));
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    const handleIssuePrescriptionError = (errorResult: P1Response<IssuePrescriptionResponse>) => {
        setIssuePrescriptionResponse(errorResult);
    };

    const handleIssuePrescription = async () => {
        try {
            setLoading(true);
            setShowP1MessagePopup(false);
            if (!appointmentId || !appointmentDetails) return;

            const { data } = await prescriptionsApi.issuePrescription({
                ...mapAppointmentDetailsToPrescription(appointmentDetails),
                prescriptions: prescriptions.map(
                    mapPrescriptionToIssuePrescription,
                ),
                isDoctorOrderMode: isMedicNurse && prescriptionType === PRESCRIPTION_TYPES.CONTINUATION,
            }, prescriptionDraftId);

            
            if (data.isSuccess && data.result.results.every(r => r.results.length === 0)) {
                history.push(PRESCRIPTION_ISSUED_PAGE, { appointmentId });
            }
            else {
                setShowP1MessagePopup(true);
                handleIssuePrescriptionError(data);
                sentryService.capturePrescriptionException(params.appointmentId, data);
            }
           
        } catch (error : any) {                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                      
            openAlert({
                severity: 'error',
                message: errorT('unexpectedErrorOccurred'),
            });
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const handleCloseP1MessagePopup = () => {
        if(issuePrescriptionResponse?.isSuccess) {
            window.close();
        } else {
            setShowP1MessagePopup(false);
        }
    };

    const insuranceInfoComponent = (
        <PatientInsurancePill 
            onClick={() => setSelectedTab(findTab('EWUS'))}
        />
    );

    const renderDrugDatabaseTab = () => (
        <>
            <h2 style={{ marginBottom: 0 }}>
                Lek {prescriptionInEditIndex}
            </h2>
            {prescriptionInEditId ? (
                <EditPrescriptionForm
                    initialValues={prescriptionInEdit || undefined}
                    onSubmit={handleSubmitEditPrescriptionForm}
                    onCancel={handleCancelEditPrescriptionForm}
                    insuranceInfoComponent={insuranceInfoComponent}
                />
            ) : (
                <NewPrescriptionForm
                    onSubmit={handleSubmitPrescriptionForm}
                    insuranceInfoComponent={insuranceInfoComponent}
                />
            )}
        </>
    );

    const renderTabContent = (tabValue: SelectButtonOption['value']) => {
        switch (tabValue) {
            case 'DRUG_DATABASE':
                return renderDrugDatabaseTab();
            case 'PRESCRIPTION_HISTORY':
                return (
                    <PrescriptionHistory
                        appointmentDetails={appointmentDetails}
                    />
                );
            case 'EWUS':
                return (
                    <EwusTab
                        appointmentDetails={appointmentDetails}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <TwoColumnLayout>
            <Loading withBackdrop loading={loading || ewusLoadingStatus === 'loading'} />
            <TwoColumnLayout.Header>
                <EmptyTopBar title={topbarTitle} onCancel={() => window.close()} />
            </TwoColumnLayout.Header>
            <TwoColumnLayout.Main>
                <TwoColumnLayout.LeftColumn>
                    <Box className={classes.formContainer}>
                        <SelectButtons
                            value={selectedTab}
                            options={tabsWithTranslations}
                            onChange={setSelectedTab}
                            containerStyles={classes.tabsContainer}
                            buttonStyles={classes.selectButton}
                        />

                        <PrescriptionTabsContent
                            tabs={tabs}
                            selectedTab={selectedTab}
                            keepMountedTabValues={KEEP_MOUNTED_TAB_VALUES}
                            renderTabContent={renderTabContent}
                        />
                    </Box>
                </TwoColumnLayout.LeftColumn>
                <TwoColumnLayout.RightColumn>
                    {prescriptions.length > 0 && (
                        <Box className={classes.card}>
                            {prescriptions.map((prescription, idx) => (
                                <PrescriptionItem
                                    key={prescription.id}
                                    prescription={prescription}
                                    index={idx}
                                    onDelete={handleDeletePrescription}
                                    onEdit={(id) => handleEditPrescriptionItem(id)}
                                    verificationResults={issuePrescriptionResponse?.result?.results?.find(r => r.order === idx + 1)?.results ?? []}
                                    isInEdit={prescriptionInEditId === prescription.id}
                                />
                            ))}
                            {isMedicNurse && (
                                <RadioGroup
                                    aria-label="prescription type"
                                    name="prescriptionType"
                                    value={prescriptionType}
                                    onChange={(e) => setPrescriptionType(Number(e.target.value) as PrescriptionType)}
                                >
                                    <FormControlLabel
                                        value={PRESCRIPTION_TYPES.ORDINATION}
                                        control={<Radio size='small'/>}
                                        label={
                                            <span style={{ fontSize: 16 }}>
                                                {t('ordination')}
                                            </span>
                                        }
                                        className={classes.radioButton}
                                    />
                                    <FormControlLabel
                                        value={PRESCRIPTION_TYPES.CONTINUATION}
                                        control={<Radio size='small'/>}
                                        label={
                                            <span style={{ fontSize: 16 }}>
                                                {t('continuation')}
                                            </span>
                                        }
                                        className={classes.radioButton}
                                    />
                                </RadioGroup>
                            )}
                            <Button
                                variant={'contained'}
                                color={'secondary'}
                                style={{ marginTop: 10, fontWeight: 'bold' }}
                                onClick={handleIssuePrescription}
                                disabled={prescriptions.some(p => !isPrescriptionFilled(p)) || prescriptionInEditId !== null}
                                fullWidth
                            >
                                {t('signAndIssue')}
                            </Button>
                        </Box>
                    )}
                </TwoColumnLayout.RightColumn>
            </TwoColumnLayout.Main>
            <PrescriptionP1MessagePopup
                open={showP1MessagePopup} 
                onClose={handleCloseP1MessagePopup} 
                p1Response={issuePrescriptionResponse} 
            />
        </TwoColumnLayout>
    );
};
export default NewPrescriptionPage;
